import type { Messages } from './messages';

/**
 * Words every screen uses: actions, the four states, errors, time.
 *
 * Owned by lib. A page that wants "حفظ" uses `common.save` rather than adding
 * `tenantMail.save` — one word said one way everywhere is the whole reason the
 * catalogue exists. A page that wants a *different* sentence for its own
 * situation ("حفظ إعدادات البريد") adds it under its own prefix.
 */
export const common = {
  /* Actions */
  'common.save': 'حفظ',
  'common.saving': 'جارٍ الحفظ…',
  'common.cancel': 'إلغاء',
  'common.confirm': 'تأكيد',
  'common.close': 'إغلاق',
  'common.edit': 'تعديل',
  'common.delete': 'حذف',
  'common.create': 'إنشاء',
  'common.back': 'رجوع',
  'common.next': 'التالي',
  'common.previous': 'السابق',
  'common.search': 'بحث',
  'common.filter': 'تصفية',
  'common.clearFilters': 'مسح التصفية',
  'common.retry': 'إعادة المحاولة',
  'common.reload': 'إعادة تحميل الصفحة',
  'common.loadMore': 'تحميل المزيد',
  'common.copy': 'نسخ',
  'common.copied': 'نُسخ.',
  'common.view': 'عرض',
  'common.actions': 'إجراءات',
  'common.replace': 'استبدال',
  'common.sendTest': 'إرسال تجربة',

  /* The four states */
  'common.loading': 'جارٍ التحميل…',
  'common.empty': 'لا شيء هنا بعد',
  'common.error': 'تعذّر تحميل هذه البيانات.',
  'common.noResults': 'لا نتائج تطابق التصفية.',
  'common.saved': 'حُفظت التغييرات.',
  'common.unsaved': 'تغييرات غير محفوظة',

  /* Values */
  'common.none': '—',
  'common.listSeparator': '، ',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.on': 'مفعّل',
  'common.off': 'معطّل',
  'common.unlimited': 'بلا حد',
  /** Shown in place of a secret, which is never returned by any endpoint. */
  'common.secretSet': 'محفوظ',
  'common.secretUnset': 'غير محفوظ',

  /**
   * The boundary note (brief §0). Shown wherever content is counted, so the
   * operator reads why there is a number and no list.
   */
  'common.contentBoundary': 'الأعداد فقط — محتوى المنشأة من محادثات وقضايا ومشاريع ومستندات غير مقروء من لوحة المنصّة.',

  /* Errors — what `messageOf` falls back to when the API sent no sentence. */
  'error.sessionExpired': 'انتهت الجلسة. سجّل الدخول من جديد.',
  'error.request': 'فشل الطلب ({status}).',
  'error.unreachable': 'تعذّر الوصول إلى الخادم. تحقّق من الاتصال ثم أعد المحاولة.',
  'error.unexpected': 'حدث خطأ غير متوقع.',
  'error.forbidden': 'رتبتك لا تسمح بهذا الإجراء.',
  'error.notFound': 'لم يُعثر على المطلوب.',
  'error.server': 'تعذّر على الخادم إتمام الطلب. أعد المحاولة بعد قليل.',
  'error.rateLimited': 'محاولات كثيرة في وقت قصير. أعد المحاولة بعد {seconds} ثانية.',
  'error.rateLimitedShort': 'محاولات كثيرة في وقت قصير. أعد المحاولة بعد قليل.',
  'error.chunk': 'تعذّر تحميل هذه الصفحة لأن ملفاتها استُبدلت بإصدار أحدث. أعد تحميل الصفحة للمتابعة.',
  /* Named refusals — fallbacks for when the API sent no sentence of its own (see `messageOf`). */
  'error.slugTaken': 'هذا المعرّف أخذته منشأة أخرى للتو. اختر معرّفًا آخر.',
  'error.verticalUnknown': 'النشاط المختار غير معروف على هذا التنصيب.',
  'error.modelUnknown': 'هذا النموذج ليس ضمن ما توجّهه المنصّة.',
  'error.modelIsDefault': 'هذا هو النموذج الافتراضي للمنشأة، ولا يُعطَّل من لوحة المنصّة. تغيّر المنشأة نموذجها الافتراضي أولًا.',
  'error.modelSwitchRace': 'تغيّر النموذج الافتراضي أثناء تنفيذ هذا التعديل. أُعيد تحميل القائمة؛ حاول مجددًا.',
  'error.permissionOpensContent': 'لا تمنح المنصّة صلاحيةً تفتح محتوى الزملاء. تمنحها إدارة المنشأة نفسها.',
  'error.operatorAddress': 'هذا العنوان يخص أحد مشغّلي المنصّة، ولا يكون المشغّل عضوًا في منشأة عميل.',
  'error.openingPasswordSuperuserOnly': 'لا يعيّن كلمة مرور أولية إلا المشرف الأعلى للمنصّة. اترك الحقل فارغًا ليُدعى المدير الأول بدلًا من ذلك.',
  'error.boundary': 'تعطّلت هذه الصفحة أثناء العرض. بقية اللوحة تعمل، ويمكنك المحاولة من جديد أو الانتقال إلى صفحة أخرى.',

  /* Time */
  'time.now': 'الآن',
  'time.never': 'أبدًا',

  /* Units — `formatBytes`. ICU's own Arabic abbreviations (م.ب) are not read by anybody. */
  'unit.byte': '{value} بايت',
  'unit.kilobyte': '{value} ك.بايت',
  'unit.megabyte': '{value} م.بايت',
  'unit.gigabyte': '{value} غ.بايت',
} as const satisfies Messages<'common' | 'error' | 'time' | 'unit'>;
